import { ActivityTracker, NamespaceLoggerAdapter } from '@fluidx/telemetry';
import { OfficeWebHostNamespace } from '../Telemetry';
import { npmVersionNumber } from '../npm.package.version';
/**
 * @deprecated
 * Base class for CreateNewTracker or RenderTracker that helps FFX team track Reliability and Performance on the CreateNew or render component path.
 * The basic idea is that the host app can instantiate the tracker before the scenario starts and calls setSuccess or setFailure to log the result appropriately.
 * GenericTelemetryTracker accepts a param trackLater, that gives the host app an opportunity to create the tracker early in the lifecycle but only start perf measurement when they want to, by calling the startActivity method on the tracker.
 * Hosts can also pass hostScenarioStartTime which helps track the time between the user's fluid initiation start time and the time the current activity completed
 */
export class GenericTelemetryTrackerBase {
    constructor(activityType, logger, trackerOptions, persistedProperties) {
        this.disposeCalled = false;
        this.isAPerformanceMetric = false;
        this.activityType = activityType;
        // Create a namespace logger so the telemetry goes to Office_Fluid_OfficeWebHost_Activity_* table
        this.OWHNamespacedLogger = new NamespaceLoggerAdapter(OfficeWebHostNamespace, logger, persistedProperties);
        if (trackerOptions) {
            this.trackLater = trackerOptions.trackLater;
            this.isAPerformanceMetric = trackerOptions.isAPerformanceMetric || false;
        }
        if (!this.trackLater) {
            this.startActivityHelper();
        }
    }
    /**
     * Exposing an API to start the activity, for callers who do not want to start tracking the activity in the constructor
     * It's possible that host apps want to initialize the activity tracker to allow for passing around much earlier than when it's needed to track perf
     */
    startActivity() {
        // Making it a no-op by checking trackLater param to avoid accidental calling of startActivity when the activity tracker has already started
        if (this.trackLater) {
            this.startActivityHelper();
        }
    }
    startActivityHelper() {
        this.activityTracker = ActivityTracker.start(this.activityType, this.OWHNamespacedLogger, {
            addUserTiming: true,
            isAPerformanceMetric: this.isAPerformanceMetric
        });
    }
    /**
     * Call this method to end the activity and send the success status
     * @param props additional props that hosts would like to pass in to our telemetry.
     */
    setSuccess(props) {
        if (this.activityTracker) {
            this.activityTracker.setResult(true, props);
        }
        // Dispose the tracker since we've logged the final result of the workflow
        this.dispose();
    }
    /**
     * Call this method to end the activity and send the failure status
     * @param error Error object thrown
     */
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any
  -- TODO: Using 'any' type defeats the purpose of TypeScript. Consider replacing it with a concrete type, or 'unknown'. */
    setFailure(error) {
        this.setFailureWithAdditionalProps(error);
    }
    /**
     * Call this method to end the activity and send the failure status
     * @param error Error object thrown
     * @param additionalProps Additional properties that will be logged.
     */
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any
  -- TODO: Using 'any' type defeats the purpose of TypeScript. Consider replacing it with a concrete type, or 'unknown'. */
    setFailureWithAdditionalProps(error, props) {
        if (this.activityTracker) {
            this.activityTracker.setResult(false, { ...(props || {}), errorType: error.errorType } /* additionalProps */, error);
        }
        // Dispose the tracker since we've logged the final result of the workflow
        this.dispose();
    }
    /**
     * Method to set the logger to undefined so no further logging can be done from the tracker and disposes the underlying ActivityTracker
     * This is required for memory cleanup and to make sure no accidental logging happens when user navigates away from Fluid experience.
     */
    dispose() {
        // Exception added when eslint rule was added, this should be revisited when modifying this code
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (this) {
            /**
             * Host app may call dispose when the lifecycle of the currently tracked activity workflow finishes
             * They may also be passing the dispose method to be called on iframe unload in case the workflow does not finish. Make calling dispose more than once a no-op using disposeCalled property.
             */
            if (this.disposeCalled) {
                return;
            }
            this.disposeCalled = true;
            this.activityTracker?.dispose();
            if (this.OWHNamespacedLogger) {
                this.OWHNamespacedLogger = undefined;
            }
        }
    }
}
/**
 * Base class for CreateNewTracker or RenderTracker that helps FFX team track Reliability and Performance on the CreateNew or render component path.
 * The class includes also the office-web-host version number in the logs.
 *
 * **Note** This should be used only outside the iframe. Internally the logger will already have the owhVersion set.
 */
export class GenericTelemetryTracker extends GenericTelemetryTrackerBase {
    constructor(activityType, logger, trackerOptions) {
        super(activityType, logger, trackerOptions, { owhVersion: npmVersionNumber } /* persistedProperties */);
    }
}
//# sourceMappingURL=GenericTelemetryTracker.js.map